import { Reseller } from '@ViewModels';
import { useMemo } from 'react';

/**
 * Maps subdomains to reseller types
 */
const SUBDOMAIN_TO_RESELLER: Record<string, Reseller> = {
	cambridge: Reseller.Cambridge,
	// Add more subdomain mappings as needed
	// revgen: Reseller.RevGen,
};

/**
 * Extracts the subdomain from a hostname
 * @param hostname - The full hostname (e.g., "cambridge.levitate.ai")
 * @returns The subdomain (e.g., "cambridge") or null if none
 */
function getSubdomain(hostname: string): string | null {
	// Split the hostname by dots
	const parts = hostname.split('.');

	// If there are at least 3 parts (subdomain.domain.tld), return the first part
	// Examples:
	// - "cambridge.levitate.ai" -> "cambridge"
	// - "levitate.ai" -> null
	// - "app.levitate.ai" -> "app" (but won't match in SUBDOMAIN_TO_RESELLER)
	// - "localhost" -> null
	if (parts.length >= 3) {
		return parts[0];
	}

	return null;
}

/**
 * Hook to determine the reseller based on the current subdomain with optional account fallback.
 * Priority order:
 * 1. Subdomain (e.g., cambridge.levitate.ai -> Reseller.Cambridge)
 * 2. Account reseller field (if provided and subdomain doesn't match)
 * 3. undefined (default Levitate branding)
 *
 * This allows for consistent reseller detection across both pre-authentication
 * (where only subdomain is available) and authenticated contexts (where account data is available).
 *
 * @param accountReseller - Optional reseller value from the account object (for authenticated contexts)
 * @returns The reseller type based on subdomain or account, or undefined for default (Levitate)
 *
 * @example
 * // Pre-authentication (Login page)
 * const reseller = useResellerFromSubdomain();
 * // On cambridge.levitate.ai -> Reseller.Cambridge
 * // On levitate.ai -> undefined (default)
 *
 * @example
 * // Authenticated (NavBar with account)
 * const reseller = useResellerFromSubdomain(userSession?.account?.reseller);
 * // On cambridge.levitate.ai -> Reseller.Cambridge (from subdomain)
 * // On app.levitate.ai with account.reseller = Cambridge -> Reseller.Cambridge (from account)
 * // On levitate.ai with no account reseller -> undefined (default)
 */
export function useResellerFromSubdomain(accountReseller?: Reseller): Reseller | undefined {
	return useMemo(() => {
		if (typeof window === 'undefined') {
			return accountReseller;
		}

		const subdomain = getSubdomain(window.location.hostname);

		if (!subdomain) {
			// No subdomain found, fallback to account reseller
			return accountReseller;
		}

		// Look up the reseller for this subdomain
		const subdomainReseller = SUBDOMAIN_TO_RESELLER[subdomain.toLowerCase()];

		// If subdomain maps to a reseller, use it; otherwise fallback to account
		return subdomainReseller ?? accountReseller;
	}, [accountReseller]);
}
